import "../../../components/common/Tables/DataTable.css"
import React, {useMemo, useState} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { Permission } from "../../../redux/models/permission.model";
import { Role } from "../../../redux/models/role.model";
import { UserGroup } from "../../../redux/models/userGroup.model";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { describePermission, DescribedPermission } from "../../../utils/permissions";
import { compareRoles, roleLabel } from "../../../utils/roles";

type Order = 'asc' | 'desc';
const headers = [
  {id:'name', label:'Permission'},
  {id:'category', label:'Area'},
  {id:'description', label:'Description'},
  {id:'roles', label:'Roles'},
  {id:'user_groups', label:'User Groups'},
];

// Sorted on this page only: by the readable name, the area, the description, or how many
// roles / groups hold the permission
const compare = (a: DescribedPermission, b: DescribedPermission, column: string) => {
  const byName = a.label.localeCompare(b.label);
  switch (column) {
    case 'category':
      return a.category.localeCompare(b.category) || byName;
    case 'description':
      return (a.permission?.description ?? '').localeCompare(b.permission?.description ?? '') || byName;
    case 'roles':
      return (a.permission?.roles?.length ?? 0) - (b.permission?.roles?.length ?? 0) || byName;
    case 'user_groups':
      return (a.permission?.user_groups?.length ?? 0) - (b.permission?.user_groups?.length ?? 0) || byName;
    default:
      return byName;
  }
};

interface PermissionsTableProps {
  // Page controls (filters toggle) shown in the table's toolbar
  toolbar?: React.ReactNode;
}

const PermissionsTable: React.FC<PermissionsTableProps> = ({toolbar}) => {
  const navigate = useNavigate();
  const {data:permissions, loading:isLoading, meta} = useSelector((state: RootState) => state.permissions);
  const [searchParams, setSearchParams] = useSearchParams();

  // Table Sort
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('name');

  const rows = useMemo(() => {
    const direction = order === 'asc' ? 1 : -1;
    return (permissions ?? [])
      .map((permission: Permission) => describePermission(permission))
      .sort((a: DescribedPermission, b: DescribedPermission) => compare(a, b, orderBy) * direction);
  }, [permissions, orderBy, order]);

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(header_id);
  };

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.total_pages ?? 1;
  const total = meta?.total ?? 0;

  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  }

  const link = (href: string, label: string) => (
    <a
      href={href}
      className="dt-tag pm-chip"
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        navigate(href);
      }}
    >
      {label}
    </a>
  );

  const renderCell = (id: string, {permission, key, label, category}: DescribedPermission) => {
    switch (id) {
      case 'name':
        return (
          <span className="dt-stack">
            <span className="dt-strong">{label}</span>
            <span className="dt-sub dt-mono">{key}</span>
          </span>
        );

      case 'category':
        return <span className="pm-area">{category}</span>;

      case 'description':
        return permission?.description
          ? <span className="dt-body pm-description" title={permission.description}>{permission.description}</span>
          : <span className="dt-na">—</span>;

      case 'roles': {
        const roles = [...(permission?.roles ?? [])].sort((a: Role, b: Role) => compareRoles(a?.name, b?.name));
        return roles.length > 0 ? (
          <span className="pm-chips">
            {roles.map((role: Role) => <React.Fragment key={role.id}>{link(`/roles/${role.id}`, roleLabel(role.name))}</React.Fragment>)}
          </span>
        ) : <span className="dt-na">—</span>;
      }

      case 'user_groups': {
        const groups = [...(permission?.user_groups ?? [])].sort((a: UserGroup, b: UserGroup) => (a?.name ?? '').localeCompare(b?.name ?? ''));
        return groups.length > 0 ? (
          <span className="pm-chips">
            {groups.map((group: UserGroup) => <React.Fragment key={group.id}>{link(`/user-groups/${group.id}`, group.name)}</React.Fragment>)}
          </span>
        ) : <span className="dt-na">—</span>;
      }

      default:
        return null;
    }
  };

  return (
    <div className="dt">
      {toolbar && <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>
      </div>}

      <div className="dt-scroll">
        {isLoading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading permissions"/></div>
        ) : rows.length > 0 ? (
          <table className="dt-table pm-table">
            <thead>
              <tr>
                {headers.map((header)=>(
                  <th
                    key={header.id}
                    scope="col"
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={()=>handleRequestSort(header.id)}/>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((row: DescribedPermission)=>(
                <tr key={row.permission?.id ?? row.key}>
                  {headers.map((header)=>(
                    <td key={header.id}>
                      {renderCell(header.id, row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No Permissions Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={permissions?.length ?? 0}
        total={total}
        noun={total === 1 ? 'Permission' : 'Permissions'}
        page={page}
        totalPages={totalPages}
        loading={isLoading}
        onPage={goToPage}
      />
    </div>
  );
};

export default PermissionsTable;
